// src/components/Layout.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CHERUB_ADMIN_SSE_PATH } from "@/config/apiConstants";
import {
  checkPHAdmin,
  isHubMedOnlyUser,
  isHolaOnlyUser,
  isMedmateOnlyUser,
  isZephyrOnlyUser,
  isProviderOnlyUser,
  getProviderDefaultPath,
  canAccessConferences,
} from "@/services/adminAuthority";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import WorkOutlinedIcon from "@mui/icons-material/WorkOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import PHLogo from "@/Images/Logos/PharmacyHalo_Logo.png";
import ChemistSearchBox from "@/components/layout/ChemistSearchBox";
import QudaIcon from "@/Images/Icons/Quda_Icon_RGB.png";
import DocLinkIcon from "@/Images/Icons/DocLink_Icon_RGB.png";
import HaloPayIcon from "@/Images/Icons/HaloPay_Icon_RGB.png";
import HaloTextIcon from "@/Images/Icons/HaloText_Icon_RGB.png";
import MediDropIcon from "@/Images/Icons/MediDrop_Icon_RGB.png";
import WebbitIcon from "@/Images/Icons/Webbit_Icon_RGB.png";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";

// ---- Cherub toast notifications (global, PHAdmin only) ----

const CHERUB_TOAST_CFG = {
  callback: { bg: '#eff6ff', border: '#bfdbfe', stripe: '#0369a1', label: 'Callback request' },
  bug:      { bg: '#fef2f2', border: '#fecaca', stripe: '#dc2626', label: 'Bug report'        },
  training: { bg: '#f0fdf4', border: '#bbf7d0', stripe: '#16a34a', label: 'Training request'  },
};

function CherubToastItem({ toast, onDismiss }) {
  const cfg = CHERUB_TOAST_CFG[toast.TicketType] || CHERUB_TOAST_CFG.bug;

  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 7000);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  return (
    <div style={{
      display: 'flex', width: 320, borderRadius: 12, overflow: 'hidden',
      backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`,
      boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
    }}>
      <div style={{ width: 5, flexShrink: 0, backgroundColor: cfg.stripe }} />
      <div style={{ flex: 1, padding: '10px 12px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: cfg.stripe, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
            {cfg.label}
          </span>
          <button onClick={() => onDismiss(toast.id)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: 0, lineHeight: 1 }}>
            ✕
          </button>
        </div>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
          {toast.ChemistName || 'Unknown pharmacy'}
        </p>
        {(toast.StaffName || toast.App) && (
          <p style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
            {[toast.StaffName, toast.App].filter(Boolean).join(' · ')}
          </p>
        )}
        {toast.Description && (
          <p style={{
            margin: '4px 0 0', fontSize: 11, color: '#475569', lineHeight: 1.4,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {toast.Description}
          </p>
        )}
      </div>
    </div>
  );
}

function CherubToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
      display: 'flex', flexDirection: 'column-reverse', gap: 12, alignItems: 'flex-end',
      pointerEvents: 'none',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: 'auto' }}>
          <CherubToastItem toast={t} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
}

// ---- /Cherub toasts ----

function getActiveStyles(id) {
  switch (id) {
    case "adminFnQuda":
    case "mgrFnQuda":         return "bg-[#39c0c0] text-[#002169]";
    case "adminFnHaloPay":
    case "mgrFnHaloPay":      return "bg-[#FFAF3C] text-[#002169]";
    case "adminFnHaloText":
    case "mgrFnHaloText":     return "bg-[#FFAF3C] text-[#002169]";
    case "adminFnDocLink":
    case "mgrFnDocLink":      return "bg-[#8d679f] text-white";
    case "adminFnMediDrop":
    case "mgrFnMediDrop":     return "bg-[#e87c57] text-white";
    case "multiroute":           return "bg-[#e87c57] text-white";
    case "adminFnWebbit":     return "bg-[#083b4b] text-white";
    case "adminFnZephyrClaims": return "bg-[#38184f] text-white";
    case "adminFnCherub":     return "bg-[#0ea5e9] text-white";
    case "adminFnDialpad":    return "bg-[#E63888] text-white";
    case "adminFnCalendly":   return "bg-[#006BFF] text-white";
    case "adminFnTrialHealth":
    case "adminFnInactiveTerminals":
    case "adminFnReports":    return "bg-[#FFAF3C] text-[#002169]";
    case "hubmedAdmin":       return "bg-violet-400 text-white";
    case "holaAdmin":         return "bg-[#005760] text-[#FFF087]";
    case "zephyrAdmin":       return "bg-[#38184f] text-white";
    case "admin":             return "bg-white/20 text-white";
    default:                  return "bg-[#FFAF3C] text-[#002169]";
  }
}

function FlyoutNavItem({ item, flyoutItems, isActive, currentPath, onNavigate, collapsed }) {
  const [open, setOpen] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const handleTriggerEnter = () => {
    cancelClose();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setFlyoutTop(rect.top);
    }
    setOpen(true);
  };

  const flyoutLeft = collapsed ? 60 : 268;

  return (
    <>
      <button
        ref={triggerRef}
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={scheduleClose}
        onClick={() => onNavigate(item.path)}
        title={collapsed ? item.text : undefined}
        className={`w-full flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 rounded-lg text-left transition-all duration-150 group font-medium
                   ${isActive ? getActiveStyles(item.id) : "text-white hover:bg-[#FFAF3C]/20 hover:text-white"}`}
      >
        <span className={isActive ? "text-current" : "text-white group-hover:text-current"}>
          {item.icon}
        </span>
        {!collapsed && <span className="font-medium flex-1">{item.text}</span>}
        {!collapsed && (
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 opacity-50 flex-shrink-0">
            <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        )}
      </button>

      {open && (
        <div
          style={{ position: "fixed", left: flyoutLeft, top: flyoutTop - 8, zIndex: 9999 }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {/* Arrow pointer */}
          <div style={{
            position: "absolute", left: -6, top: 18,
            width: 0, height: 0,
            borderTop: "6px solid transparent",
            borderBottom: "6px solid transparent",
            borderRight: "6px solid #001550",
          }} />
          <div
            className="rounded-xl shadow-2xl border border-[#001550] overflow-hidden"
            style={{ backgroundColor: "#002169", minWidth: 210 }}
          >
            <div className="px-1.5 py-1.5 space-y-0.5">
              {flyoutItems.map((fi) => {
                const fiActive = currentPath === fi.path;
                return (
                  <button
                    key={fi.id}
                    onClick={() => { setOpen(false); onNavigate(fi.path); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all duration-100 font-medium text-sm
                               ${fiActive ? getActiveStyles(fi.id) : "text-white hover:bg-[#FFAF3C]/20 hover:text-white"}`}
                  >
                    <span className={fiActive ? "text-current" : "text-white opacity-80"}>
                      {fi.icon}
                    </span>
                    <span>{fi.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Layout = ({ children, setIsAuthenticated }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  const [userInfo, setUserInfo] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [authorityTick, setAuthorityTick] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      localStorage.setItem("sidebarCollapsed", String(!prev));
      return !prev;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await checkPHAdmin();
      if (!cancelled) setAuthorityTick((t) => t + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isHubMedOnlyUser()) return;
    if (!location.pathname.startsWith("/hubmed-admin")) {
      navigate("/hubmed-admin/overview", { replace: true });
    }
  }, [location.pathname, authorityTick, navigate]);

  useEffect(() => {
    if (!isHolaOnlyUser()) return;
    if (!location.pathname.startsWith("/hola-admin")) {
      navigate("/hola-admin/overview", { replace: true });
    }
  }, [location.pathname, authorityTick, navigate]);

  useEffect(() => {
    if (!isMedmateOnlyUser()) return;
    if (!location.pathname.startsWith("/medmate-admin")) {
      navigate("/medmate-admin/overview", { replace: true });
    }
  }, [location.pathname, authorityTick, navigate]);

  useEffect(() => {
    if (!isZephyrOnlyUser()) return;
    if (!location.pathname.startsWith("/zephyr-admin")) {
      navigate("/zephyr-admin/overview", { replace: true });
    }
  }, [location.pathname, authorityTick, navigate]);

  // Multi-provider user (e.g. HubMed + Zephyr, no PH Admin):
  // redirect away from manager/admin paths they should not access.
  useEffect(() => {
    if (!isProviderOnlyUser()) return;
    // Already covered by single-provider redirects above
    if (isHubMedOnlyUser() || isHolaOnlyUser() || isMedmateOnlyUser() || isZephyrOnlyUser()) return;
    const providerPaths = ["/hubmed-admin", "/hola-admin", "/medmate-admin", "/zephyr-admin"];
    // Allow conferences path if the user has conferences access
    if (canAccessConferences() && location.pathname.startsWith("/admin-functions/conferences")) return;
    const onProviderPath = providerPaths.some((p) => location.pathname.startsWith(p));
    if (!onProviderPath) {
      navigate(getProviderDefaultPath(), { replace: true });
    }
  }, [location.pathname, authorityTick, navigate]);

  useEffect(() => {
    const onResize = () => setIsOpen(window.innerWidth >= 1024);
    onResize();
    window.addEventListener("resize", onResize);

    const storedUserInfo = localStorage.getItem("googleUserInfo");
    if (storedUserInfo) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        setUserInfo(parsedUserInfo);
        setImageError(false); // Reset image error when user info changes
      } catch (e) {
        console.error("Error parsing user info:", e);
      }
    }
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [cherubToasts, setCherubToasts] = useState([]);

  const addCherubToast = useCallback((data) => {
    setCherubToasts(prev => [{ ...data, id: Date.now() + Math.random() }, ...prev].slice(0, 5));
  }, []);

  const dismissCherubToast = useCallback((id) => {
    setCherubToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('IsPHAdmin') !== 'true') return;
    const token = localStorage.getItem('jwtToken');
    if (!token) return;
    const url = `${import.meta.env.VITE_APP_DATA_API_URL}${CHERUB_ADMIN_SSE_PATH}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.addEventListener('ticket_created', (e) => {
      try { addCherubToast(JSON.parse(e.data)); } catch {}
    });
    return () => es.close();
  }, [authorityTick, addCherubToast]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("googleUserInfo");
      localStorage.removeItem("userData");
      localStorage.removeItem("tempGoogleUserInfo");
      localStorage.removeItem("token");
      Object.keys(localStorage).forEach((k) => {
        if (/(token|auth|user|google)/i.test(k)) localStorage.removeItem(k);
      });
    } finally {
      setIsAuthenticated?.(false);
      navigate("/login", { replace: true });
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const hubMedNavItem = {
    text: "HubMed Admin",
    id: "hubmedAdmin",
    path: "/hubmed-admin/overview",
    icon: <MedicalServicesIcon className="w-5 h-5" />,
  };

  const holaNavItem = {
    text: "Hola Admin",
    id: "holaAdmin",
    path: "/hola-admin/overview",
    icon: <HealthAndSafetyOutlinedIcon className="w-5 h-5" />,
  };

  const medmateNavItem = {
    text: "Medmate Admin",
    id: "medmateAdmin",
    path: "/medmate-admin/overview",
    icon: <MedicalServicesIcon className="w-5 h-5" />,
  };

  const overviewIcon = (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <path
        fill="currentColor"
        d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
      />
    </svg>
  );

  const subscriptionsNavItem = {
    text: "Subscriptions",
    id: "subscriptions",
    path: "/subs",
    icon: <SubscriptionsIcon className="w-5 h-5" />,
  };

  const defaultMainNavItems = [
    {
      text: "Overview",
      id: "overview",
      path: "/overview",
      icon: overviewIcon,
    },
    {
      text: "Quda Overview",
      id: "quda",
      path: "/quda",
      icon: <img src={QudaIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "DocLink Overview",
      id: "doclink",
      path: "/doclink",
      icon: <img src={DocLinkIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    subscriptionsNavItem,
  ];

  const isPHAdmin = sessionStorage.getItem("IsPHAdmin") === "true";
  const isHubMedAdmin = sessionStorage.getItem("IsHubMedAdmin") === "true";
  const isZephyrAdmin = sessionStorage.getItem("IsZephyrAdmin") === "true";
  const isHolaAdmin = sessionStorage.getItem("IsHolaAdmin") === "true";
  const isMedmateAdmin = sessionStorage.getItem("IsMedmateAdmin") === "true";
  const conferencesAccess = canAccessConferences(); // PH Admin OR is_conferences_user
  const hubMedOnly = isHubMedOnlyUser();
  const holaOnly = isHolaOnlyUser();
  const medmateOnly = isMedmateOnlyUser();
  const zephyrOnly = isZephyrOnlyUser();
  const providerOnly = isProviderOnlyUser();

  const dialpadNavItem = {
    text: "Dialpad",
    id: "adminFnDialpad",
    path: "/admin-functions/dialpad/dashboard",
    icon: <PhoneOutlinedIcon className="w-5 h-5" />,
    flyoutItems: [
      { text: "Dashboard",   id: "adminFnDialpadDash",        path: "/admin-functions/dialpad/dashboard",   icon: <PhoneOutlinedIcon className="w-5 h-5" /> },
      { text: "Unallocated", id: "adminFnDialpadUnallocated", path: "/admin-functions/dialpad/unallocated", icon: <ReportProblemOutlinedIcon className="w-5 h-5" /> },
      { text: "Users",       id: "adminFnDialpadUsers",       path: "/admin-functions/dialpad/admin-users", icon: <GroupOutlinedIcon className="w-5 h-5" /> },
    ],
  };

  const calendlyNavItem = {
    text: "Calendly",
    id: "adminFnCalendly",
    path: "/admin-functions/calendly",
    icon: <EventOutlinedIcon className="w-5 h-5" />,
  };

  const conferencesNavItem = {
    text: "Conferences",
    id: "adminFnConferences",
    path: "/admin-functions/conferences",
    icon: <EventOutlinedIcon className="w-5 h-5" />,
  };

  const workflowNavItem = {
    text: "Workflow",
    id: "workflow",
    path: "/workflow/job-board",
    icon: <ViewKanbanOutlinedIcon className="w-5 h-5" />,
    flyoutItems: [
      { text: "Job Board",   id: "workflowJobBoard",   path: "/workflow/job-board",   icon: <WorkOutlinedIcon className="w-5 h-5" />        },
      { text: "Onboarding",  id: "workflowOnboarding", path: "/workflow/onboarding",  icon: <PersonAddOutlinedIcon className="w-5 h-5" />    },
      { text: "Group Deals", id: "workflowGroupDeals", path: "/workflow/group-deals", icon: <LocalOfferOutlinedIcon className="w-5 h-5" />   },
    ],
  };

  const adminNavItem = {
    text: "Admin Tools",
    id: "admin",
    path: "/admin",
    icon: <AdminPanelSettingsOutlinedIcon className="w-5 h-5" />,
  };

  const adminFunctionSidebarNavItems = [
    {
      text: "Overview",
      id: "adminFnOverview",
      path: "/admin-functions/overview",
      icon: overviewIcon,
    },
    {
      text: "Quda",
      id: "adminFnQuda",
      path: "/admin-functions/quda",
      icon: <img src={QudaIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "HaloPay",
      id: "adminFnHaloPay",
      path: "/admin-functions/halopay",
      icon: <img src={HaloPayIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "HaloText",
      id: "adminFnHaloText",
      path: "/admin-functions/halotext",
      icon: <img src={HaloTextIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "DocLink",
      id: "adminFnDocLink",
      path: "/admin-functions/doclink",
      icon: <img src={DocLinkIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "MediDrop",
      id: "adminFnMediDrop",
      path: "/admin-functions/medidrop",
      icon: <img src={MediDropIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "Webbit",
      id: "adminFnWebbit",
      path: "/admin-functions/webbit",
      icon: <img src={WebbitIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "Zephyr Claims",
      id: "adminFnZephyrClaims",
      path: "/admin-functions/zephyr-claims",
      icon: <img src={ZephyrIconWhite} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "CherubAI Gaps",
      id: "adminFnCherub",
      path: "/admin-functions/cherub",
      icon: <QuestionAnswerOutlinedIcon className="w-5 h-5" />,
    },
  ];

  const managerFunctionSidebarNavItems = [
    {
      text: "Overview",
      id: "mgrFnOverview",
      path: "/manager-functions/overview",
      icon: overviewIcon,
    },
    {
      text: "Quda",
      id: "mgrFnQuda",
      path: "/manager-functions/quda",
      icon: <img src={QudaIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "HaloPay",
      id: "mgrFnHaloPay",
      path: "/manager-functions/halopay",
      icon: <img src={HaloPayIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "HaloText",
      id: "mgrFnHaloText",
      path: "/manager-functions/halotext",
      icon: <img src={HaloTextIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "DocLink",
      id: "mgrFnDocLink",
      path: "/manager-functions/doclink",
      icon: <img src={DocLinkIcon} className="w-5 h-5 object-contain" alt="" />,
    },
    {
      text: "MediDrop",
      id: "mgrFnMediDrop",
      path: "/manager-functions/medidrop",
      icon: <img src={MediDropIcon} className="w-5 h-5 object-contain" alt="" />,
    },
  ];

  const multirouteNavItem = {
    text: "Multiroute",
    id: "multiroute",
    path: "/multiroute",
    icon: <LocalShippingOutlinedIcon className="w-5 h-5" />,
  };

  const zephyrNavItem = {
    text: "Zephyr Admin",
    id: "zephyrAdmin",
    path: "/zephyr-admin/overview",
    icon: <img src={ZephyrIconWhite} className="w-5 h-5 object-contain" alt="" />,
  };

  const sidebarItems = hubMedOnly
    ? [hubMedNavItem]
    : holaOnly
    ? [holaNavItem]
    : medmateOnly
    ? [medmateNavItem]
    : zephyrOnly
    ? [zephyrNavItem]
    : (() => {
        if (isPHAdmin) {
          const [overviewItem, ...appItems] = adminFunctionSidebarNavItems;
          const flyoutItems = appItems.filter(i => i.id !== 'adminFnCherub');
          const overviewWithFlyout = { ...overviewItem, flyoutItems };
          // PH Admins always see HubMed, Hola, and Medmate regardless of individual flags
          const items = [
            overviewWithFlyout, workflowNavItem, multirouteNavItem, dialpadNavItem, calendlyNavItem, conferencesNavItem, adminNavItem,
            hubMedNavItem, holaNavItem, medmateNavItem,
          ];
          if (isZephyrAdmin) items.push(zephyrNavItem);
          return items;
        }
        // Provider-only user (HubMed/Hola/Medmate, no PH Admin):
        // show ONLY their provider portal items — no Overview or Workflow.
        if (providerOnly) {
          const items = [];
          if (isHubMedAdmin) items.push(hubMedNavItem);
          if (isHolaAdmin) items.push(holaNavItem);
          if (isMedmateAdmin) items.push(medmateNavItem);
          if (isZephyrAdmin) items.push(zephyrNavItem);
          if (conferencesAccess) items.push(conferencesNavItem);
          return items;
        }
        // Regular group manager
        const [mgrOverviewItem, ...mgrAppItems] = managerFunctionSidebarNavItems;
        const mgrOverviewWithFlyout = { ...mgrOverviewItem, flyoutItems: mgrAppItems };
        const items = [mgrOverviewWithFlyout, workflowNavItem, multirouteNavItem];
        if (isHubMedAdmin) items.push(hubMedNavItem);
        if (isHolaAdmin) items.push(holaNavItem);
        if (isMedmateAdmin) items.push(medmateNavItem);
        if (isZephyrAdmin) items.push(zephyrNavItem);
        // Non-PH Admin managers with explicit conferences access see only the Conferences item
        if (conferencesAccess) items.push(conferencesNavItem);
        return items;
      })();

  const isAdminFunctionNavActive = (item) => {
    if (item.id === "adminFnOverview") {
      return item.flyoutItems
        ? location.pathname.startsWith("/admin-functions/") && !location.pathname.startsWith("/admin-functions/dialpad") && !location.pathname.startsWith("/admin-functions/calendly") && !location.pathname.startsWith("/admin-functions/conferences")
        : location.pathname === "/admin-functions/overview" ||
          location.pathname.startsWith("/admin-functions/chemist/");
    }
    if (item.id === "adminFnDialpad") {
      return location.pathname.startsWith("/admin-functions/dialpad");
    }
    if (item.id === "adminFnCalendly") {
      return location.pathname.startsWith("/admin-functions/calendly");
    }
    if (item.id === "adminFnConferences") {
      return location.pathname.startsWith("/admin-functions/conferences");
    }
    if (item.id === "mgrFnOverview") {
      return (
        location.pathname === "/manager-functions/overview" ||
        location.pathname.startsWith("/manager-functions/chemist/")
      );
    }
    return location.pathname === item.path;
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 lg:hidden z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed z-40 inset-y-0 left-0 shadow-xl border-r border-[#001550]
                    transform transition-all duration-200 ease-out
                    ${sidebarCollapsed ? "w-14" : "w-64"}
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:static lg:translate-x-0`}
        style={{ backgroundColor: "#002169" }}
        aria-label="Sidebar"
      >
        <div className="h-full flex flex-col">

          {/* Logo + search + collapse toggle */}
          <div className={`border-b border-[#001550] ${sidebarCollapsed ? "px-2 py-3" : "px-4 pt-3 pb-3"}`}>
            {/* Logo row */}
            <div className={`flex items-center ${sidebarCollapsed ? "flex-col gap-2" : "justify-between mb-3"}`}>
              <img
                src={PHLogo}
                alt="Pharmacy Halo Logo"
                className={sidebarCollapsed ? "w-8 h-8 object-contain" : "w-3/4 h-auto object-contain"}
                style={{ filter: "brightness(0) invert(1)" }}
              />
              {/* ||| toggle */}
              <button
                onClick={toggleSidebar}
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="flex items-center justify-center w-7 h-7 rounded-md text-white/40 hover:bg-white/15 hover:text-white/90 transition flex-shrink-0"
              >
                <svg viewBox="0 0 18 18" className="w-4 h-4">
                  <rect x="2"  y="3" width="2.5" height="12" rx="1.2" fill="currentColor"/>
                  <rect x="7.75" y="3" width="2.5" height="12" rx="1.2" fill="currentColor"/>
                  <rect x="13.5" y="3" width="2.5" height="12" rx="1.2" fill="currentColor"/>
                </svg>
              </button>
            </div>
            {!sidebarCollapsed && <ChemistSearchBox sidebar />}
          </div>

          {/* Nav items */}
          <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
            {sidebarItems.map((item) => {
              const active =
                item.id === "workflow"
                  ? location.pathname.startsWith("/workflow")
                  : item.id === "hubmedAdmin"
                  ? location.pathname.startsWith("/hubmed-admin")
                  : item.id === "holaAdmin"
                    ? location.pathname.startsWith("/hola-admin")
                  : item.id === "zephyrAdmin"
                    ? location.pathname.startsWith("/zephyr-admin")
                    : item.id === "admin"
                      ? location.pathname.startsWith("/admin") &&
                        !location.pathname.startsWith("/admin-functions")
                      : String(item.id).startsWith("adminFn") || String(item.id).startsWith("mgrFn")
                        ? isAdminFunctionNavActive(item)
                        : location.pathname === item.path;

              if (item.flyoutItems) {
                return (
                  <FlyoutNavItem
                    key={item.id}
                    item={item}
                    flyoutItems={item.flyoutItems}
                    isActive={active}
                    currentPath={location.pathname}
                    onNavigate={handleNavigation}
                    collapsed={sidebarCollapsed}
                  />
                );
              }

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  title={sidebarCollapsed ? item.text : undefined}
                  className={`w-full flex items-center ${sidebarCollapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"} rounded-lg text-left transition-all duration-150 group font-medium
                             ${active ? getActiveStyles(item.id) : "text-white hover:bg-[#FFAF3C]/20 hover:text-white"}`}
                >
                  <span className={`${active ? "text-current" : "text-white group-hover:text-current"} flex-shrink-0`}>
                    {item.icon}
                  </span>
                  {!sidebarCollapsed && <span className="font-medium">{item.text}</span>}
                </button>
              );
            })}
          </nav>

          {/* Footer: user info + collapse + logout */}
          <div className={`border-t border-[#001550] ${sidebarCollapsed ? "p-2 space-y-1" : "p-3 space-y-2"}`}>

            {/* User info */}
            {!sidebarCollapsed ? (
              <div className="flex items-center gap-3 px-3 py-2 bg-[#001550] rounded-lg">
                {userInfo?.picture && !imageError ? (
                  <img
                    src={userInfo.picture}
                    alt={`${userInfo.name || "User"}'s profile`}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#FFAF3C] flex items-center justify-center text-[#002169] font-semibold text-sm flex-shrink-0">
                    {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{userInfo?.name || "User"}</p>
                  <p className="text-xs text-[#93c5fd] truncate">{userInfo?.email || ""}</p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center py-1" title={userInfo?.name || "User"}>
                {userInfo?.picture && !imageError ? (
                  <img
                    src={userInfo.picture}
                    alt="profile"
                    className="w-8 h-8 rounded-full object-cover"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#FFAF3C] flex items-center justify-center text-[#002169] font-semibold text-sm">
                    {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}
                  </div>
                )}
              </div>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              title={sidebarCollapsed ? "Logout" : undefined}
              className={`w-full flex items-center ${sidebarCollapsed ? "justify-center px-0" : "gap-3 px-3"} py-2 rounded-lg text-left text-red-300 hover:bg-red-900/40 hover:text-red-200 transition`}
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0">
                <path fill="currentColor" d="M16 13v-2H7V7l-5 5 5 5v-4h9zM20 3h-8v2h8v14h-8v2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" />
              </svg>
              {!sidebarCollapsed && <span className="font-medium">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main column must be a flex column with min-h-0 so children can scroll and no phantom space */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        <header className="bg-white shadow-sm border-b px-4 py-2 flex items-center sticky top-0 z-20 lg:hidden">
          <button
            onClick={() => setIsOpen((s) => !s)}
            className="p-2 rounded-lg hover:bg-gray-100 transition flex-shrink-0"
            aria-label="Toggle sidebar"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <path
                fill="currentColor"
                d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zM3 6v2h18V6H3z"
              />
            </svg>
          </button>
        </header>

        {/* Content area */}
        <main className="flex-1 min-w-0 overflow-y-auto p-2">{children}</main>
      </div>

      <CherubToastStack toasts={cherubToasts} onDismiss={dismissCherubToast} />
    </div>
  );
};

export default Layout;
